/**
 * Bundles & Quotations.
 *
 * Deliberately plain. Shivam: "Please do not overcomplicate the UI now." Two
 * tabs, a builder that shows the total as you type, and a list of documents.
 *
 * The total shown here is not computed in this file. Every figure comes from
 * POST /commercial/price-preview, which runs the same price_lines() the issued
 * quotation runs — so what the operator sees and what the customer receives
 * cannot drift. The only arithmetic in this component is none.
 *
 * BASE PRICES IS ADDRESSABLE. `/commercial?tab=prices&price=<item>` opens the
 * Base prices tab on that item, highlighted, with its editor open for an
 * operator who may write prices. It is where every "Not in the default price
 * book" remedy points (Create Tenant, Edit Tenant's add-ons, a refused
 * basket) — `basePricesLink.js` builds those links and parses this one. The
 * unit pre-selected for an unpriced item is the server's (`unit`, which falls
 * back to the catalogue's `default_unit`: per tenant for an add-on such as
 * Surveys or Landing Pages). Nothing is saved until the operator saves.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { humanise } from '../tenants/productLabels';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { PermissionGate, usePermission } from '../components';
import { commercialTarget } from './basePricesLink';
import { useAdminAuth } from '../auth/AdminAuthContext';
import EmptyState from '../../../components/admin/EmptyState';
import { Skeleton } from '../../../components/ui/skeleton';
import {
  Package, FileText, Plus, Trash2, Send, Loader2, AlertTriangle, Lock, RefreshCw,
  Tag, Check, X,
} from 'lucide-react';

const ADMIN_API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;

const money = (amount, currency) =>
  amount === null || amount === undefined
    ? '—'
    : new Intl.NumberFormat(undefined, {
        style: 'currency', currency: currency || 'USD',
      }).format(amount);

const STATUS_TONE = {
  draft: 'bg-slate-100 text-slate-700',
  issued: 'bg-blue-100 text-blue-800',
  accepted: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-red-100 text-red-800',
  expired: 'bg-amber-100 text-amber-800',
  superseded: 'bg-slate-100 text-slate-500',
};

// Why a line has no price, in words an operator can act on. The backend sends
// the code; spelling it out here keeps the reason legible without the API
// having to carry prose.
const UNPRICED_REASON = {
  no_base_price_set: 'no base price set — set one on the Base Prices tab, or type a price here',
  entitlement_is_not_offered_for_sale: 'this is not something we sell',
  base_price_term_cannot_convert_to_quoted_term: 'its base price cannot convert to this term',
  // D5: the price book resolves through four distinct reasons, and they have
  // four different fixes. Before this they fell through to the raw code —
  // legible, but `no_book` told an operator nothing about what to do, and it
  // is the one that means every product on every screen is unpriced rather
  // than just this line.
  no_book: 'there is no active default price book — set one under Price Books before pricing anything',
  no_entry: 'this product has no entry in the price book — set its price on the Base Prices tab, or type one here',
  no_match: 'the price book has entries for this product, but none applies to this region, segment, term or quantity',
  ambiguous: 'two equally specific price book entries both apply — narrow one of them, or retire it',
};

const emptyLine = () => ({
  entitlement_code: '', quantity: 1, unit_price_override: '', override_reason: '',
  line_discount_percent: 0,
});

const CommercialPage = () => {
  const { getAdminToken } = useAdminAuth();
  const auth = useCallback(
    () => ({ headers: { Authorization: `Bearer ${getAdminToken()}` } }),
    [getAdminToken],
  );

  const [sellable, setSellable] = useState([]);
  const [products, setProducts] = useState([]);
  const [productTotals, setProductTotals] = useState({});
  const [editing, setEditing] = useState(null);   // entitlement_code being priced
  const [draftPrice, setDraftPrice] = useState('');
  const [draftTerm, setDraftTerm] = useState('monthly');
  const [draftUnit, setDraftUnit] = useState('seat');
  const [bundles, setBundles] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  // Which tab is open is held here rather than left to the Tabs default.
  // Refreshing after a save briefly swaps the whole subtree for the skeleton,
  // and an uncontrolled Tabs remounts on its default — so saving a price threw
  // the operator back to Quotations.
  //
  // The URL may ask for a tab and an item (`?tab=prices&price=surveys`): the
  // link every unpriced-item remedy carries. Read once, at mount.
  const [searchParams] = useSearchParams();
  const [target] = useState(() => commercialTarget(searchParams));
  const [tab, setTab] = useState(target.tab || 'quotations');
  const { allowed: canWritePrices } = usePermission('plan:write');
  const focusedOnce = useRef(false);

  // The builder, shared by both tabs — a quotation is a bundle plus a customer.
  const [lines, setLines] = useState([emptyLine()]);
  const [term, setTerm] = useState('monthly');
  const [pricingMode, setPricingMode] = useState('list_price');
  const [discountPercent, setDiscountPercent] = useState('');
  const [discountAmount, setDiscountAmount] = useState('');
  const [fixedTotal, setFixedTotal] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [pricing, setPricing] = useState(false);

  const [bundleOpen, setBundleOpen] = useState(false);
  const [bundleCode, setBundleCode] = useState('');
  const [bundleName, setBundleName] = useState('');
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [quoteNotes, setQuoteNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const loadAll = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const [structure, bundleRes, quoteRes, priceRes] = await Promise.all([
        axios.get(`${ADMIN_API}/catalog/structure`, auth()),
        axios.get(`${ADMIN_API}/commercial/bundles`, auth()),
        axios.get(`${ADMIN_API}/commercial/quotations`, auth()),
        axios.get(`${ADMIN_API}/commercial/product-prices`, auth()),
      ]);
      setSellable(
        (structure.data.categories || [])
          .flatMap((category) => category.entitlements)
          // Add-ons ARE offered as their own line: they are the priced thing,
          // and their owning products (CRM Console, File Manager) are INCLUDED,
          // so the owner can never be a line item to select them beneath.
          // `derive_entitlements` attaches an add-on line to its owner's
          // entitlement, so it never becomes an app_code — which is what stops
          // it turning into an app-switcher tile.
          .filter((entitlement) => entitlement.sellable),
      );
      setBundles(bundleRes.data.bundles || []);
      setQuotations(quoteRes.data.quotations || []);
      setProducts(priceRes.data.products || []);
      setProductTotals(priceRes.data.totals || {});
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load the commercial catalogue');
    } finally {
      setLoading(false);
    }
  }, [auth]);

  // After a mutation the data is refetched without blanking the page, so the
  // open tab, the scroll position and the operator's place all survive.
  const refresh = useCallback(() => loadAll({ quiet: true }), [loadAll]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // OPEN THE ASKED-FOR ITEM ONCE ITS ROW EXISTS. Once: a refresh after a save
  // must not reopen the editor the operator just closed. Opened only for an
  // operator who may write prices; anyone else still sees the row highlighted
  // and the gated "Set price" button that says why it is disabled.
  const focusItem = target.item;
  useEffect(() => {
    if (!focusItem || focusedOnce.current || !products.length) return;
    const product = products.find((p) => p.entitlement_code === focusItem);
    if (!product) return;
    focusedOnce.current = true;
    if (canWritePrices) beginEdit(product);
    const el = typeof document !== 'undefined'
      && document.querySelector(`[data-testid="base-price-row-${focusItem}"]`);
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'center' });
    // `beginEdit` is a plain function redefined every render; the product
    // list, the item and the permission are what decide this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, focusItem, canWritePrices]);

  // Price whenever the builder changes. The server owns every number.
  useEffect(() => {
    const usable = lines.filter((line) => line.entitlement_code);
    if (!usable.length) { setPreview(null); setPreviewError(null); return undefined; }

    const timer = setTimeout(async () => {
      setPricing(true);
      try {
        const { data } = await axios.post(`${ADMIN_API}/commercial/price-preview`, {
          items: usable.map((line) => ({
            entitlement_code: line.entitlement_code,
            quantity: Number(line.quantity) || 1,
            ...(line.unit_price_override !== '' && {
              unit_price_override: Number(line.unit_price_override),
            }),
            ...(line.override_reason && { override_reason: line.override_reason }),
            line_discount_percent: Number(line.line_discount_percent) || 0,
          })),
          term,
          pricing_mode: pricingMode,
          ...(pricingMode === 'discount_percent' && { discount_percent: Number(discountPercent) || 0 }),
          ...(pricingMode === 'discount_amount' && { discount_amount: Number(discountAmount) || 0 }),
          ...(pricingMode === 'fixed_total' && { fixed_total: Number(fixedTotal) || 0 }),
        }, auth());
        setPreview(data);
        setPreviewError(null);
      } catch (error) {
        setPreview(null);
        setPreviewError(
          typeof error.response?.data?.detail === 'string'
            ? error.response.data.detail
            : 'These lines cannot be priced together',
        );
      } finally {
        setPricing(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [lines, term, pricingMode, discountPercent, discountAmount, fixedTotal, auth]);

  const commercialTerms = () => ({
    term,
    pricing_mode: pricingMode,
    ...(pricingMode === 'discount_percent' && { discount_percent: Number(discountPercent) || 0 }),
    ...(pricingMode === 'discount_amount' && { discount_amount: Number(discountAmount) || 0 }),
    ...(pricingMode === 'fixed_total' && { fixed_total: Number(fixedTotal) || 0 }),
    items: lines.filter((line) => line.entitlement_code).map((line) => ({
      entitlement_code: line.entitlement_code,
      quantity: Number(line.quantity) || 1,
      ...(line.unit_price_override !== '' && {
        unit_price_override: Number(line.unit_price_override),
      }),
      ...(line.override_reason && { override_reason: line.override_reason }),
      line_discount_percent: Number(line.line_discount_percent) || 0,
    })),
  });

  const resetBuilder = () => {
    setLines([emptyLine()]);
    setPricingMode('list_price');
    setDiscountPercent(''); setDiscountAmount(''); setFixedTotal('');
    setPreview(null); setPreviewError(null);
  };

  const saveBundle = async () => {
    setSaving(true);
    try {
      await axios.post(`${ADMIN_API}/commercial/bundles`, {
        bundle_code: bundleCode.trim().toUpperCase().replace(/[^A-Z0-9_]/g, '_'),
        name: bundleName.trim(),
        ...commercialTerms(),
      }, auth());
      toast.success(`${bundleName} saved`);
      setBundleOpen(false); setBundleCode(''); setBundleName(''); resetBuilder();
      refresh();
    } catch (error) {
      toast.error(readDetail(error));
    } finally {
      setSaving(false);
    }
  };

  const saveQuotation = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${ADMIN_API}/commercial/quotations`, {
        customer_name: customerName.trim(),
        ...(customerEmail.trim() && { customer_email: customerEmail.trim() }),
        ...(quoteNotes.trim() && { notes: quoteNotes.trim() }),
        ...commercialTerms(),
      }, auth());
      toast.success(`${data.quotation_number} created as a draft`);
      setQuoteOpen(false); setCustomerName(''); setCustomerEmail(''); setQuoteNotes('');
      resetBuilder();
      refresh();
    } catch (error) {
      toast.error(readDetail(error));
    } finally {
      setSaving(false);
    }
  };

  const issue = async (quotation) => {
    try {
      await axios.post(
        `${ADMIN_API}/commercial/quotations/${quotation.id}/issue`, {}, auth());
      toast.success(`${quotation.quotation_number} issued — its prices are now fixed`);
      refresh();
    } catch (error) {
      toast.error(readDetail(error));
    }
  };

  const decide = async (quotation, status) => {
    try {
      await axios.post(
        `${ADMIN_API}/commercial/quotations/${quotation.id}/decision`, { status }, auth());
      toast.success(`${quotation.quotation_number} marked ${status}`);
      refresh();
    } catch (error) {
      toast.error(readDetail(error));
    }
  };

  const revise = async (quotation) => {
    try {
      const { data } = await axios.post(
        `${ADMIN_API}/commercial/quotations/${quotation.id}/revise`, {}, auth());
      toast.success(`${data.quotation_number} created; ${quotation.quotation_number} is superseded`);
      refresh();
    } catch (error) {
      toast.error(readDetail(error));
    }
  };

  const savePrice = async (product) => {
    const value = Number(draftPrice);
    if (draftPrice === '' || Number.isNaN(value) || value < 0) {
      toast.error('Enter a price, or clear it to leave the product unpriced');
      return;
    }
    try {
      await axios.put(
        `${ADMIN_API}/commercial/product-prices/${product.entitlement_code}`,
        { base_price: value, currency: 'USD', term: draftTerm, unit: draftUnit },
        auth(),
      );
      toast.success(`${product.label} base price set`);
      setEditing(null);
      refresh();
    } catch (error) {
      toast.error(readDetail(error));
    }
  };

  const clearPrice = async (product) => {
    try {
      await axios.delete(
        `${ADMIN_API}/commercial/product-prices/${product.entitlement_code}`, auth());
      toast.success(`${product.label} is unpriced again`);
      setEditing(null);
      refresh();
    } catch (error) {
      toast.error(readDetail(error));
    }
  };

  const beginEdit = (product) => {
    setEditing(product.entitlement_code);
    setDraftPrice(product.base_price ?? '');
    setDraftTerm(product.term || 'monthly');
    // The server's unit: the saved one, else the book's, else the catalogue's
    // pre-selection (per tenant for an add-on). Never a hard-coded "seat".
    setDraftUnit(product.unit || product.default_unit || 'seat');
  };

  // An optional add-on is priced as its own line but sold beneath a product;
  // the grid says which, from the server's `add_on_of_label`, so "Surveys" is
  // legible as Engage's add-on rather than a product of its own.
  const ownerLabel = (product) => (product.add_on_of
    ? (product.add_on_of_label || humanise(product.add_on_of)) : null);
  const focusedProduct = focusItem
    ? products.find((p) => p.entitlement_code === focusItem) : null;

  const startFromBundle = (bundle) => {
    setLines((bundle.items || []).map((item) => ({
      ...emptyLine(), ...item,
      unit_price_override: item.unit_price_override ?? '',
      override_reason: item.override_reason ?? '',
    })));
    setTerm(bundle.term || 'monthly');
    setPricingMode(bundle.pricing_mode || 'list_price');
    setDiscountPercent(bundle.discount_percent ?? '');
    setDiscountAmount(bundle.discount_amount ?? '');
    setFixedTotal(bundle.fixed_total ?? '');
    setQuoteOpen(true);
  };

  const updateLine = (index, patch) =>
    setLines((current) => current.map((line, i) => (i === index ? { ...line, ...patch } : line)));

  const builder = (
    <div className="space-y-4">
      <div className="space-y-2">
        {lines.map((line, index) => {
          const priced = preview?.lines?.[index];
          return (
            <div key={index} className="grid grid-cols-12 gap-2 items-start">
              <div className="col-span-4">
                <Select
                  value={line.entitlement_code}
                  onValueChange={(value) => updateLine(index, { entitlement_code: value })}
                >
                  <SelectTrigger><SelectValue placeholder="Choose a product" /></SelectTrigger>
                  <SelectContent>
                    {sellable.map((entitlement) => (
                      <SelectItem key={entitlement.code} value={entitlement.code}>
                        {entitlement.label}
                        {entitlement.pricing === 'tbd' && ' — price TBD'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Input
                className="col-span-2" type="number" min="1" value={line.quantity}
                onChange={(event) => updateLine(index, { quantity: event.target.value })}
                placeholder="Qty"
              />
              <Input
                className="col-span-2" type="number" min="0" step="0.01"
                value={line.unit_price_override}
                onChange={(event) => updateLine(index, { unit_price_override: event.target.value })}
                placeholder={
                  priced?.base_price != null ? `base ${priced.base_price}` : 'price'
                }
              />
              <Input
                className="col-span-1" type="number" min="0" max="100"
                value={line.line_discount_percent}
                onChange={(event) => updateLine(index, { line_discount_percent: event.target.value })}
                placeholder="% off"
              />
              <div className="col-span-2 pt-2 pr-1 text-right text-sm tabular-nums">
                {priced?.priced
                  ? money(priced.line_total, preview?.currency)
                  : <span className="text-amber-600">—</span>}
              </div>
              <div className="col-span-1 pt-1">
                <Button
                  variant="ghost" size="sm"
                  onClick={() => setLines((current) =>
                    current.length === 1 ? [emptyLine()] : current.filter((_, i) => i !== index))}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              {priced && !priced.priced && (
                <p className="col-span-12 -mt-1 text-xs text-amber-700">
                  {priced.label}: {UNPRICED_REASON[priced.unpriced_reason] || priced.unpriced_reason}
                </p>
              )}
            </div>
          );
        })}
        <Button variant="outline" size="sm" onClick={() => setLines((c) => [...c, emptyLine()])}>
          <Plus className="h-4 w-4 mr-1" /> Add a line
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label className="text-xs">Billed</Label>
          <Select value={term} onValueChange={setTerm}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="yearly">Yearly</SelectItem>
              <SelectItem value="one_time">One time</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Pricing</Label>
          <Select value={pricingMode} onValueChange={setPricingMode}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="list_price">List price</SelectItem>
              <SelectItem value="discount_percent">Discount %</SelectItem>
              <SelectItem value="discount_amount">Discount amount</SelectItem>
              <SelectItem value="fixed_total">Negotiated total</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          {pricingMode !== 'list_price' && (
            <>
              <Label className="text-xs">
                {pricingMode === 'discount_percent' ? 'Percent off'
                  : pricingMode === 'discount_amount' ? 'Amount off' : 'Total'}
              </Label>
              <Input
                type="number" min="0"
                value={pricingMode === 'discount_percent' ? discountPercent
                  : pricingMode === 'discount_amount' ? discountAmount : fixedTotal}
                onChange={(event) => (pricingMode === 'discount_percent'
                  ? setDiscountPercent(event.target.value)
                  : pricingMode === 'discount_amount'
                    ? setDiscountAmount(event.target.value)
                    : setFixedTotal(event.target.value))}
              />
            </>
          )}
        </div>
      </div>

      <div className="rounded-lg border bg-slate-50 p-4">
        {previewError ? (
          <p className="text-sm text-red-700 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" /> {previewError}
          </p>
        ) : !preview ? (
          <p className="text-sm text-slate-500">Choose a product to see the price.</p>
        ) : (
          <>
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Subtotal</span>
              <span className="tabular-nums">{money(preview.subtotal, preview.currency)}</span>
            </div>
            {preview.discount !== 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">
                  {preview.discount > 0 ? 'Discount' : 'Above list price'}
                </span>
                <span className="tabular-nums">
                  {money(Math.abs(preview.discount), preview.currency)}
                </span>
              </div>
            )}
            <div className="mt-1 flex justify-between border-t pt-2 font-semibold">
              <span>Total {term === 'yearly' ? 'per year' : term === 'monthly' ? 'per month' : ''}</span>
              <span className="tabular-nums">
                {pricing ? <Loader2 className="h-4 w-4 animate-spin" />
                  : money(preview.total, preview.currency)}
              </span>
            </div>
            {preview.unpriced?.length > 0 && (
              <p className="mt-3 flex items-start gap-2 text-xs text-amber-700">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                {preview.unpriced.length} line{preview.unpriced.length > 1 ? 's have' : ' has'} no
                price. A quotation cannot be issued until each one has an explicit price — a missing
                price is not zero.
              </p>
            )}
            {preview.warnings?.map((warning) => (
              <p key={warning} className="mt-2 text-xs text-amber-700">{warning}</p>
            ))}
          </>
        )}
      </div>
    </div>
  );

  if (loading) {
    return <div className="p-6 space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24 w-full" />)}</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Bundles &amp; Quotations</h1>
          <p className="text-sm text-slate-600">
            Combine products, price them, and send a customer a figure that stays fixed.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => loadAll()}>
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="quotations">
            <FileText className="h-4 w-4 mr-1" /> Quotations ({quotations.length})
          </TabsTrigger>
          <TabsTrigger value="bundles">
            <Package className="h-4 w-4 mr-1" /> Bundles ({bundles.length})
          </TabsTrigger>
          <TabsTrigger value="prices">
            <Tag className="h-4 w-4 mr-1" /> Base prices
            {productTotals.unpriced > 0 && (
              <Badge variant="secondary" className="ml-2">{productTotals.unpriced} unpriced</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="prices" className="space-y-4">
          {focusedProduct && (
            <p className="rounded-md border border-primary/40 bg-primary/5 px-3 py-2 text-sm"
               data-testid="base-price-focus">
              {focusedProduct.is_priced
                ? <>{focusedProduct.label} is priced in the default price book.</>
                : <>
                    <strong>{focusedProduct.label}</strong> is not in the default price
                    book yet. Set its base price below — Create Tenant and Edit Tenant read
                    it from the same book.
                  </>}
            </p>
          )}
          {focusItem && !focusedProduct && products.length > 0 && (
            <p className="text-sm text-muted-foreground" data-testid="base-price-focus-missing">
              {humanise(focusItem)} is not a product offered for sale, so it has no base price.
            </p>
          )}

          <p className="text-sm text-slate-600">
            One standard price per product, set here and changed here — no deployment
            involved. A product with no price stays <strong>Unpriced</strong>, which is not
            the same as free: a quotation cannot be issued against it until somebody
            states a figure. Changing a price affects new bundles and quotations only;
            an issued quotation keeps the numbers it was issued with.
          </p>

          <div className="rounded-lg border divide-y">
            {products.map((product) => (
              <div key={product.entitlement_code}
                   className={`flex flex-wrap items-center gap-3 p-3 ${
                     product.entitlement_code === focusItem ? 'bg-primary/5 ring-1 ring-primary/40' : ''}`}
                   data-testid={`base-price-row-${product.entitlement_code}`}>
                <div className="min-w-[12rem]">
                  {/* The product name only. The entitlement code is an
                      identifier (`chatbot`, `ai_features`), not something an
                      operator pricing a product needs to read. */}
                  <p className="font-medium text-slate-900">{product.label}</p>
                  {ownerLabel(product) && (
                    <p className="text-xs text-muted-foreground"
                       data-testid={`base-price-addon-of-${product.entitlement_code}`}>
                      Optional add-on to {ownerLabel(product)}
                    </p>
                  )}
                </div>

                {editing === product.entitlement_code ? (
                  <>
                    <Input
                      className="w-28" type="number" min="0" step="0.01" autoFocus
                      data-testid={`base-price-input-${product.entitlement_code}`}
                      value={draftPrice}
                      onChange={(event) => setDraftPrice(event.target.value)}
                      placeholder="0.00"
                    />
                    <Select value={draftUnit} onValueChange={setDraftUnit}>
                      <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="seat">per seat</SelectItem>
                        <SelectItem value="tenant">per tenant</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={draftTerm} onValueChange={setDraftTerm}>
                      <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">per month</SelectItem>
                        <SelectItem value="yearly">per year</SelectItem>
                        <SelectItem value="one_time">one time</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button size="sm" onClick={() => savePrice(product)}
                            data-testid={`base-price-save-${product.entitlement_code}`}>
                      <Check className="h-4 w-4 mr-1" /> Save
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </>
                ) : (
                  <>
                    {product.is_priced ? (
                      <span className="tabular-nums font-medium">
                        {money(product.base_price, product.currency)}
                        <span className="ml-1 text-xs font-normal text-slate-500">
                          per {product.unit} / {product.term === 'one_time' ? 'one time'
                            : product.term.replace('ly', '')}
                        </span>
                      </span>
                    ) : (
                      <Badge variant="secondary" className="text-amber-700"
                             data-testid={`base-price-unpriced-${product.entitlement_code}`}>
                        Unpriced
                      </Badge>
                    )}
                    <div className="ml-auto flex gap-2">
                      <PermissionGate permission="plan:write">
                        <Button size="sm" variant="outline" onClick={() => beginEdit(product)}>
                          {product.is_priced ? 'Change' : 'Set price'}
                        </Button>
                      </PermissionGate>
                      {product.is_priced && (
                        <PermissionGate permission="plan:write">
                          <Button size="sm" variant="ghost" onClick={() => clearPrice(product)}
                            title="Back to Unpriced — not the same as setting zero">
                            Clear
                          </Button>
                        </PermissionGate>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>

          <p className="text-xs text-slate-500">
            {productTotals.priced || 0} of {productTotals.sellable || 0} products priced.
          </p>
        </TabsContent>

        <TabsContent value="quotations" className="space-y-4">
          <PermissionGate permission="plan:write">
            <Button onClick={() => { resetBuilder(); setQuoteOpen(true); }}>
              <Plus className="h-4 w-4 mr-1" /> New quotation
            </Button>
          </PermissionGate>

          {quotations.length === 0 ? (
            <EmptyState
              icon={FileText}
              title="No quotations yet"
              description="A quotation freezes its prices when you issue it, so it stays a record of what the customer was offered."
            />
          ) : (
            <div className="space-y-2">
              {quotations.map((quotation) => (
                <Card key={quotation.id}>
                  <CardContent className="flex flex-wrap items-center gap-4 py-4">
                    <div className="min-w-[10rem]">
                      <p className="font-medium text-slate-900">{quotation.quotation_number}</p>
                      <p className="text-sm text-slate-600">{quotation.customer_name}</p>
                    </div>
                    <Badge className={STATUS_TONE[quotation.status] || ''}>
                      {quotation.status}
                      {quotation.status !== 'draft' && <Lock className="ml-1 h-3 w-3" />}
                    </Badge>
                    {quotation.revision > 1 && (
                      <span className="text-xs text-slate-500">
                        revision {quotation.revision} of {quotation.revision_of}
                      </span>
                    )}
                    <div className="ml-auto text-right">
                      <p className="font-semibold tabular-nums">
                        {money(quotation.total, quotation.currency)}
                      </p>
                      <p className="text-xs text-slate-500">
                        {quotation.term}
                        {quotation.is_live_preview && ' · price today'}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {quotation.status === 'draft' && (
                        <PermissionGate permission="plan:write">
                          <Button
                            size="sm"
                            onClick={() => issue(quotation)}
                            disabled={quotation.issuable === false}
                            title={quotation.issuable === false
                              ? 'A line has no price. Set one before issuing.' : undefined}
                          >
                            <Send className="h-4 w-4 mr-1" /> Issue
                          </Button>
                        </PermissionGate>
                      )}
                      {quotation.status === 'issued' && (
                        <PermissionGate permission="plan:write">
                          <Button size="sm" variant="outline"
                            onClick={() => decide(quotation, 'accepted')}>Accepted</Button>
                        </PermissionGate>
                      )}
                      {['issued', 'accepted'].includes(quotation.status) && (
                        <PermissionGate permission="plan:write">
                          <Button size="sm" variant="ghost" onClick={() => revise(quotation)}>
                            Revise
                          </Button>
                        </PermissionGate>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="bundles" className="space-y-4">
          <PermissionGate permission="plan:write">
            <Button onClick={() => { resetBuilder(); setBundleOpen(true); }}>
              <Plus className="h-4 w-4 mr-1" /> New bundle
            </Button>
          </PermissionGate>

          {bundles.length === 0 ? (
            <EmptyState
              icon={Package}
              title="No bundles yet"
              description="Pick any combination of products. The bundle starts from their base prices; add a discount or a negotiated total from there."
            />
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {bundles.map((bundle) => (
                <Card key={bundle.id}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">{bundle.name}</CardTitle>
                    <CardDescription>{bundle.bundle_code}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex flex-wrap gap-1">
                      {(bundle.items || []).map((item) => (
                        <Badge key={item.entitlement_code} variant="secondary">
                          {products.find((p) => p.entitlement_code === item.entitlement_code)?.label
                            || humanise(item.entitlement_code)}
                          {' '}× {item.quantity}
                        </Badge>
                      ))}
                    </div>
                    <div className="flex items-baseline justify-between">
                      <span className="text-sm text-slate-600">
                        {bundle.pricing?.error ? 'Cannot be priced' : `per ${bundle.term}`}
                      </span>
                      <span className="font-semibold tabular-nums">
                        {bundle.pricing?.error ? '—'
                          : money(bundle.pricing?.total, bundle.pricing?.currency)}
                      </span>
                    </div>
                    {bundle.pricing?.error && (
                      <p className="text-xs text-amber-700">{bundle.pricing.error}</p>
                    )}
                    <PermissionGate permission="plan:write">
                      <Button size="sm" variant="outline" onClick={() => startFromBundle(bundle)}>
                        Quote this
                      </Button>
                    </PermissionGate>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={bundleOpen} onOpenChange={setBundleOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>New bundle</DialogTitle>
            <DialogDescription>Products sold together at a price you set once.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Name</Label>
              <Input value={bundleName} onChange={(e) => setBundleName(e.target.value)} />
            </div>
            <div>
              <Label>Code</Label>
              <Input value={bundleCode} onChange={(e) => setBundleCode(e.target.value)}
                placeholder="GROWTH_PACK" />
            </div>
          </div>
          {builder}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBundleOpen(false)}>Cancel</Button>
            <Button
              onClick={saveBundle}
              disabled={saving || !bundleName.trim() || bundleCode.trim().length < 2
                || !preview}
            >
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Save bundle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={quoteOpen} onOpenChange={setQuoteOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>New quotation</DialogTitle>
            <DialogDescription>
              Saved as a draft. Prices are fixed only when you issue it.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Customer</Label>
              <Input value={customerName} onChange={(e) => setCustomerName(e.target.value)} />
            </div>
            <div>
              <Label>Email <span className="text-slate-400">(optional)</span></Label>
              <Input value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} />
            </div>
          </div>
          {builder}
          <div>
            <Label>Notes <span className="text-slate-400">(optional)</span></Label>
            <Textarea value={quoteNotes} onChange={(e) => setQuoteNotes(e.target.value)} rows={2} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuoteOpen(false)}>Cancel</Button>
            <Button onClick={saveQuotation} disabled={saving || !customerName.trim() || !preview}>
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Save draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/** FastAPI sends validation errors as a list; showing "[object Object]" helps nobody. */
function readDetail(error) {
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg).join('; ');
  return 'That did not work';
}

export default CommercialPage;
